import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { ROLE_CHIP_STYLES } from "screens/manager/constants";

// No literal "stethoscope" or "nurse cap" icon exists in @mui/icons-material,
// so these are the closest role-appropriate stand-ins, kept in sync with the
// same role -> color mapping the Manager > Users table chips already use.
const ROLE_ICONS = {
  doctor: MedicalServicesIcon,
  nurse: HealthAndSafetyIcon,
  admin: AdminPanelSettingsIcon,
};

// Overlays a small role-specific icon half on/half off the top-right edge
// of a staff avatar so the role reads at a glance. Positioned manually
// (not via MUI's <Badge>) because Badge sizes its anchor transform for its
// own default small-dot content, which doesn't track an arbitrary avatar
// size.
//
// The whole badge (circle + icon) is sized as a percentage of the avatar
// itself, rather than a fixed pixel/font size — avatars across the app are
// sized in a mix of vw, dvh, and px, so a fixed badge size looked right on
// whichever avatar it was tuned against and wrong everywhere else. It
// auto-scales correctly on any avatar without a per-usage size prop.
//
// Two CSS gotchas to keep in mind if this gets touched again:
// 1. The wrapper below sets width/height: "max-content" (a definite value,
//    unlike the default "auto") specifically so percentage width/height/
//    top/right on the absolutely-positioned badge resolve against *this*
//    box's actual rendered avatar size — without it, percentage height on
//    an auto-sized container is ambiguous per spec and browsers can end up
//    resolving it against a totally different (often much taller) ancestor,
//    which is exactly what produced an oval badge instead of a circle.
// 2. `width: "38%"` + `aspectRatio: 1` (not a second `height: "38%"`) is
//    what actually guarantees a circle — deriving height from width
//    sidesteps the percentage-height ambiguity in (1) entirely rather than
//    relying on both resolving consistently.
//
// Renders children unchanged if the user has no role with an icon mapping
// (e.g. still loading, or a custom role outside doctor/nurse/admin).
const RoleAvatarBadge = ({ children }) => {
  const userRoles = useSelector((state) => state?.auth?.userRoles);
  const primaryRole = (userRoles || []).find(
    (r) => r.is_active !== false && ROLE_ICONS[r.code]
  );
  const RoleIcon = primaryRole ? ROLE_ICONS[primaryRole.code] : null;

  if (!RoleIcon) return children;

  const badgeColor = ROLE_CHIP_STYLES[primaryRole.code]?.color || "#374151";

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        width: "max-content",
        height: "max-content",
        lineHeight: 0,
      }}
    >
      {children}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          right: "15%",
          transform: "translate(50%, -50%)",
          width: "38%",
          aspectRatio: "1",
          minWidth: "16px",
          borderRadius: "50%",
          backgroundColor: badgeColor,
          border: "2px solid #fff",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <RoleIcon sx={{ width: "70%", height: "70%", color: "#fff" }} />
      </Box>
    </Box>
  );
};

export default RoleAvatarBadge;
